import api from "@/api";
import converters from "@/converters";
import type { CategoryFragment } from "@/graphql";
import type { CategoryInput } from "@/graphql/generated-types";
import isDataReady from "@/helpers/requests/isDataReady";
import type { ICachedData, ICategoryAPI } from "@/interfaces";
import type { CategoryTypes } from "@/types";
import { computed, ref } from "vue";

const DEFAULT_DATA: ICachedData<CategoryFragment[]> = {
  status: "none",
  data: [],
};

const API_MAP: Record<CategoryTypes, ICategoryAPI> = {
  activities: api.activityCategories,
  news: api.newsCategories,
};

const dataMap = ref<Record<CategoryTypes, ICachedData<CategoryFragment[]>>>({
  activities: { ...DEFAULT_DATA },
  news: { ...DEFAULT_DATA },
});

export function useCategories(type: CategoryTypes, isInit: boolean = false) {
  const currentAPI = API_MAP[type];

  const init = async () => {
    const item = dataMap.value[type];

    if (item.status === "loaded" || isInit) {
      return;
    }

    item.status = "loading";
    const data = await currentAPI.getCategories();
    item.data = data || [];
    item.status = "loaded";
  };

  init();

  const categories = computed(() => dataMap.value[type].data);

  const categoriesList = computed(() => {
    return dataMap.value[type].data
      .map(converters.categories.toMultiSelectItem)
      .toSorted((a, b) => a.title.localeCompare(b.title));
  });

  const categoriesOptions = computed(() => {
    return dataMap.value[type].data
      .map(converters.categories.toOption)
      .toSorted((a, b) => a.title.localeCompare(b.title));
  });

  const loading = computed(() => {
    return dataMap.value[type].status === "loading";
  });

  const status = computed(() => dataMap.value[type].status);

  const isReady = () => isDataReady(status);

  const refetch = async () => {
    const data = await currentAPI.getCategories();
    dataMap.value[type].data = data || [];
  };

  const save = async (input: CategoryInput) => {
    const response = await currentAPI.save(input);
    await refetch();
    return response;
  };

  const deleteItem = async (id: number) => {
    const response = await currentAPI.deleteItem(id.toString());
    await refetch();
    return response;
  };

  const getEntityCountOnRelatedCategory = async (id: number) => {
    return await currentAPI.getEntityCountOnRelatedCategory(id);
  };

  return {
    categories,
    categoriesList,
    categoriesOptions,
    loading,
    refetch,
    isReady,
    save,
    deleteItem,
    getEntityCountOnRelatedCategory,
  };
}
